// Admin · invite/create staff client.
//
// Wraps the FastAPI /api/admin/invite-staff endpoint introduced in
// Module 45. The endpoint uses the Supabase service role to look up
// auth.users by email and either:
//   * attach the existing user to this org's profile table, or
//   * create + invite a fresh user via supabase.auth.admin.invite.
//
// The browser never holds the service role key — the FastAPI proxy does
// the privileged work and returns only what the UI needs.

import { supabase } from "@/lib/supabase";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export async function adminInviteStaff({
  email,
  fullName = null,
  organizationId,
  role = "teacher",
  redirectTo = null,
}) {
  if (!email || !organizationId) {
    throw new Error("Email and organization required");
  }
  const { data: sessionData, error: sessErr } = await supabase.auth.getSession();
  if (sessErr || !sessionData?.session?.access_token) {
    throw new Error("Sign in again to invite staff");
  }

  const res = await fetch(`${API_BASE}/api/admin/invite-staff`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionData.session.access_token}`,
    },
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      full_name: fullName?.trim() || null,
      organization_id: organizationId,
      role,
      redirect_to: redirectTo,
    }),
  });

  if (!res.ok) {
    // Service-role-not-configured short-circuit: the FastAPI proxy
    // returns 503 with a helpful detail, but we surface a stable
    // message so Staff.jsx can fall back to the legacy attach-existing
    // path without scraping the body. Avoids the classic body-stream
    // double-read pitfall: status check runs before any body read.
    if (res.status === 503) {
      const err = new Error(
        "Staff invites need configuration. Ask your dev to set SUPABASE_SERVICE_ROLE_KEY in the backend env.",
      );
      err.code = "SERVICE_ROLE_MISSING";
      err.status = 503;
      throw err;
    }
    // For any other non-2xx, read the body exactly once and try to
    // pull a `detail` field out of JSON (FastAPI shape). Falls back
    // to plain text without re-reading the stream.
    let detail = "";
    try {
      const raw = await res.text();
      try {
        const j = JSON.parse(raw);
        detail = j.detail || raw;
      } catch {
        detail = raw;
      }
    } catch {
      detail = "";
    }
    const err = new Error(detail || `Invite failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
